import api from './axios';

/** Database page phase 3. Contract: CITS-Server/docs/migration-runner-api.md ("Phase 3"). */

const P = '/super-admin/database/ops';

export interface OpsServer {
  key: string;
  label: string;
  kind: 'registry' | 'tenant';
  host: string;
  port: number;
  companies: { id: string; name: string; code?: string; database: string; status?: string }[];
}

export interface LiveProcess {
  id: number;
  user: string | null;
  host: string | null;
  db: string | null;
  company: { id: string; name: string } | null;
  command: string | null;
  state: string | null;
  seconds: number;
  query: string | null;
  system: boolean;
  waiting_for_lock: boolean;
}

export interface LockWait {
  waiting_id: number | null;
  blocking_id: number | null;
  object: string | null;
  lock_type: string | null;
  seconds: number | null;
}

export interface LiveServer {
  server: OpsServer;
  at: string;
  ok: boolean;
  error?: string;
  connections?: { current: number | null; max: number | null; max_used: number | null; percent: number | null };
  locks?: { source: 'performance_schema' | 'innodb' | 'processlist'; waits: LockWait[] };
  summary?: { total: number; active: number; sleeping: number; longest_seconds: number; lock_waits: number };
  processes?: LiveProcess[];
}

export interface Disk { path: string; total_bytes: number; used_bytes: number; free_bytes: number; percent: number | null }

export interface SizeDb { db: string; company: { id: string; name: string } | null; tables: number; data_bytes: number; index_bytes: number; free_bytes: number; rows: number; total_bytes: number }
export interface SizeTable { db: string; name: string; engine: string | null; data_bytes: number; index_bytes: number; free_bytes: number; row_estimate: number; total_bytes: number }

export interface SizeServer {
  server: OpsServer;
  ok: boolean;
  error?: string;
  disk: Disk | null;
  total_bytes?: number;
  databases: SizeDb[];
  tables: SizeTable[];
}

export interface GrowthDb { db: string; first_bytes: number; last_bytes: number; added_bytes: number; per_day_bytes: number | null; days_covered: number; series: { date: string; bytes: number }[] }
export interface GrowthTable { db: string; name: string; added_bytes: number; per_day_bytes: number | null; last_bytes: number }

export interface SizeOverview {
  servers: SizeServer[];
  growth: { days: number; databases: GrowthDb[]; tables: GrowthTable[] } | null;
  growth_available: boolean;
  suggestions: { db: string; table: string; total_bytes: number; per_day_bytes: number | null; reason: string; suggestion: string }[];
}

export interface SlowQuery {
  digest: string;
  db: string;
  company: { id: string; name: string } | null;
  query: string | null;
  count: number;
  total_ms: number;
  avg_ms: number;
  max_ms: number;
  rows_examined_avg: number;
  rows_sent_avg: number;
  no_index_used: number;
  last_seen: string | null;
  suggestion: { table: string; columns: string[]; sql: string; why: string } | null;
}

export interface SlowServer {
  server: OpsServer;
  ok: boolean;
  enabled: boolean | null;
  reason?: string | null;
  help?: string;
  error?: string;
  queries: SlowQuery[];
}

export interface DataFixRun {
  id: string;
  fix_path: string;
  target_db: string;
  status: 'running' | 'success' | 'failed' | 'undone' | 'undo_failed';
  rows_matched: number;
  rows_snapshotted: number;
  rows_changed: number | null;
  error_message: string | null;
  executed_by: string | null;
  started_at: string;
  finished_at: string | null;
  undo_until: string | null;
  undone_by: string | null;
  undone_at: string | null;
}

export interface DataFix {
  path: string;
  title: string;
  scope: 'tenant' | 'registry';
  table: string | null;
  pk: string;
  preview: string | null;
  statements: string[];
  hash: string;
  errors: string[];
  runs: DataFixRun[];
}

export interface FixPreview {
  fix: { path: string; title: string | null; table: string; pk: string; hash: string; statements: string[] };
  results: { target: string; ok: boolean; rows?: number; too_many?: boolean; sample_keys?: string[]; sample_rows?: Record<string, unknown>[]; error?: string; code?: string | null }[];
}

export interface FixRunResult {
  fix: { path: string; title: string | null; hash: string };
  results: { run_id: string; target: string; status: 'success' | 'failed'; rows_matched?: number; rows_snapshotted?: number; rows_changed?: number; error?: string; code?: string | null }[];
  skipped: number;
}

export interface CompanyExport {
  id: string;
  company_id: string;
  company_name: string | null;
  status: 'queued' | 'running' | 'ready' | 'failed' | 'expired';
  storage: string | null;
  size_bytes: number | null;
  tables_count: number | null;
  rows_count: number | null;
  expires_at: string | null;
  error_message: string | null;
  requested_by: string | null;
  created_at: string;
  finished_at: string | null;
}

export interface CompanyDeletion {
  id: string;
  company_id: string;
  company_name: string | null;
  database_name: string | null;
  status: 'running' | 'done' | 'failed';
  backup_id: string | null;
  backup_storage: string | null;
  backup_keep_until: string | null;
  backup_purged_at: string | null;
  files_removed: number | null;
  files_note: string | null;
  reason: string | null;
  error_message: string | null;
  requested_by: string | null;
  approved_by: string | null;
  created_at: string;
  finished_at: string | null;
}

export interface OpsSettings {
  alerts: {
    disk_percent: number;
    lock_minutes: number;
    backup_failed: boolean;
    drift_daily: boolean;
    batch_failed: boolean;
    recipients: 'owners' | 'all';
    extra_emails: string[];
  };
  run_window: { enabled: boolean; start: string; end: string };
}

export interface OpsAlert {
  id: string;
  metric_type: string;
  alert_name: string;
  alert_message: string;
  severity: 'info' | 'warning' | 'critical';
  company_id: string | null;
  occurrence_count: number;
  triggered_at: string;
  last_seen_at: string | null;
  notification_sent: number | boolean;
  is_resolved: number | boolean;
  resolved_at: string | null;
  resolved_by: string | null;
}

export interface Schedule {
  id: string;
  run_at: string;
  migration_ids: number[];
  targets: string[];
  options: Record<string, unknown> | null;
  label: string | null;
  status: 'scheduled' | 'started' | 'cancelled' | 'failed';
  batch_id: string | null;
  error_message: string | null;
  created_by: string | null;
  created_at: string;
  started_at: string | null;
}

export interface ScheduleList {
  run_window: OpsSettings['run_window'];
  timezone: string;
  next_window: string | null;
  schema_ready: boolean;
  schedules: Schedule[];
}

export const opsApi = {
  servers: () => api.get<{ servers: OpsServer[] }>(`${P}/servers`).then((r) => r.data.servers),

  live: (server?: string) => api.get<{ servers: LiveServer[] }>(`${P}/live`, { params: server ? { server } : {} }).then((r) => r.data.servers),
  kill: (server: string, processId: number) =>
    api.post<{ stopped: boolean }>(`${P}/live/kill`, { server, processId }).then((r) => r.data),

  size: (server?: string) => api.get<SizeOverview>(`${P}/size`, { params: server ? { server } : {} }).then((r) => r.data),
  snapshot: () => api.post<{ servers: number; rows: number }>(`${P}/size/snapshot`).then((r) => r.data),

  slow: (params: { server?: string; sort?: 'avg' | 'total' | 'count' | 'examined' } = {}) =>
    api.get<{ servers: SlowServer[] }>(`${P}/slow`, { params }).then((r) => r.data.servers),

  dataFixes: () => api.get<{ schema_ready: boolean; fixes: DataFix[] }>(`${P}/data-fixes`).then((r) => r.data),
  previewFix: (path: string, targets: string[]) => api.post<FixPreview>(`${P}/data-fixes/preview`, { path, targets }).then((r) => r.data),
  runFix: (path: string, targets: string[], hash: string, stopOnFailure = true) =>
    api.post<FixRunResult>(`${P}/data-fixes/run`, { path, targets, hash, stopOnFailure }).then((r) => r.data),
  undoFix: (runId: string) =>
    api.post<{ restored: number; dropped_columns: string[] }>(`${P}/data-fixes/runs/${runId}/undo`).then((r) => r.data),

  exports: (companyId?: string) =>
    api.get<{ schema_ready: boolean; exports: CompanyExport[] }>(`${P}/company-data/exports`, { params: companyId ? { companyId } : {} }).then((r) => r.data),
  startExport: (companyId: string) => api.post<{ id: string }>(`${P}/company-data/exports`, { companyId }).then((r) => r.data),
  deletions: () => api.get<{ schema_ready: boolean; deletions: CompanyDeletion[] }>(`${P}/company-data/deletions`).then((r) => r.data),
  deleteCompanyData: (body: { companyId: string; confirmCode: string; reason: string }, approvalId?: string) =>
    api.post<{ id: string }>(`${P}/company-data/delete`, { ...body, approvalId }).then((r) => r.data),

  settings: () => api.get<{ settings: OpsSettings; timezone: string }>(`${P}/settings`).then((r) => r.data),
  saveSettings: (settings: OpsSettings) => api.put<{ settings: OpsSettings }>(`${P}/settings`, { settings }).then((r) => r.data.settings),

  alerts: () => api.get<{ alerts: OpsAlert[]; settings: OpsSettings['alerts'] }>(`${P}/alerts`).then((r) => r.data),
  checkAlerts: () => api.post<{ raised: number; repeated: number; resolved: number; emailed: number }>(`${P}/alerts/check`, {}).then((r) => r.data),
  resolveAlert: (id: string) => api.post(`${P}/alerts/${id}/resolve`).then((r) => r.data),

  schedules: () => api.get<ScheduleList>(`${P}/schedules`).then((r) => r.data),
  schedule: (body: { runAt: string; migrationIds: number[]; targets: string[]; label?: string; options?: Record<string, unknown> }) =>
    api.post<Schedule>(`${P}/schedules`, body).then((r) => r.data),
  cancelSchedule: (id: string) => api.post(`${P}/schedules/${id}/cancel`).then((r) => r.data),
};
